import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, Loader2, CreditCard, AlertTriangle, CheckCircle2, FileSignature } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOLDE_LOOKUPS = { Essentielle: "solde_essentielle", Médium: "solde_medium", Prestige: "solde_prestige" };

export default function SoldePage() {
  const { token } = useParams();
  const [contrat, setContrat] = useState(null);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/contrats/${token}`)
      .then((r) => setContrat(r.data))
      .catch(() => setError("Ce lien de paiement est invalide ou a expiré."));
  }, [token]);

  const payer = async () => {
    setPaying(true);
    try {
      const lookup = contrat.solde_lookup_key || SOLDE_LOOKUPS[contrat.formule];
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Le paiement n'a pas pu être initialisé", { description: "Réessayez dans un instant." });
      setPaying(false);
    }
  };

  const solde = contrat ? contrat.solde ?? contrat.montant_total * 0.7 : 0;

  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="solde-page">
      <header className="border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="solde-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-xl px-4 py-16 sm:px-6">
        {!contrat && !error && (
          <div className="flex h-64 items-center justify-center" data-testid="solde-loading">
            <Loader2 size={26} className="animate-spin text-cyan-400" />
          </div>
        )}

        {error && (
          <div className="rounded-3xl border border-amber-400/25 bg-[#0E1524]/80 p-10 text-center" data-testid="solde-error">
            <AlertTriangle size={36} className="mx-auto text-amber-400" />
            <h1 className="font-display mt-4 text-2xl font-extrabold text-slate-50">Lien invalide</h1>
            <p className="mt-2 text-sm text-slate-400">{error}</p>
          </div>
        )}

        {contrat && (
          <div className="rounded-3xl border border-cyan-500/20 bg-[#0E1524]/80 p-8 text-center backdrop-blur-xl sm:p-10" data-testid="solde-card">
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Solde de votre prestation</p>
            <h1 className="font-display mt-3 text-3xl font-extrabold text-slate-50">
              {contrat.type_evenement} — Formule {contrat.formule}
            </h1>
            <p className="mt-3 text-sm text-slate-400">
              {contrat.client_nom}
              {contrat.date_evenement
                ? ` · ${format(parseISO(contrat.date_evenement), "d MMMM yyyy", { locale: fr })}`
                : ""}
            </p>

            {contrat.statut !== "signe" && (
              <div className="mt-6 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-4" data-testid="solde-unsigned-notice">
                <p className="flex items-center justify-center gap-2 text-sm text-amber-300">
                  <FileSignature size={15} /> Le contrat n'est pas encore signé.
                </p>
                <Link
                  to={`/signature/${token}`}
                  data-testid="solde-sign-link"
                  className="mt-2 inline-block text-sm font-semibold text-cyan-300 hover:text-cyan-200"
                >
                  Signer le contrat d'abord →
                </Link>
              </div>
            )}

            <div className="my-7 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent" />

            <p className="text-sm text-slate-400">Reste à régler (70 %)</p>
            <p className="font-display text-gradient mt-1 text-5xl font-black" data-testid="solde-amount">
              {solde.toLocaleString("fr-FR")} €
            </p>

            <button
              onClick={payer}
              disabled={paying}
              data-testid="solde-pay-button"
              className="bg-brand-gradient mx-auto mt-8 flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.3)] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
            >
              {paying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
              {paying ? "Redirection…" : "Régler le solde en ligne"}
            </button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-600">
              <CheckCircle2 size={12} /> Paiement sécurisé par Stripe. Déjà réglé par virement ? Ignorez cette page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
